import { useState } from 'react';
import { HoruzProtocolChulakServiceSessionGuardTags } from '@ver.id/libs.internal.horuz';
import { useTranslation } from '../../i18n/i18n';
import { type AppInfo, type RequestType } from '../../types';
import DesktopLayout from '../../layouts/DesktopLayout';
import FaqModal, { type FaqTopic } from '../../components/FaqModal';
import InstallInstructionsModal from '../../components/InstallInstructionsModal';
import DesktopFlowFooter from '../../components/DesktopFlowFooter';
import NeedHelpSection from '../../components/NeedHelpSection';
import QrCodeCard from '../../components/QrCodeCard';

type Props = {
  tags: HoruzProtocolChulakServiceSessionGuardTags;
  app?: AppInfo;
  requestType?: RequestType;
  onCancel: () => void;
  onBack?: () => void;
};

export default function DesktopAgentStartPage({
  tags,
  app,
  requestType,
  onCancel,
  onBack,
}: Props) {
  const { t } = useTranslation();
  const appName = app?.nameWithArticle || app?.name || 'your identity app';
  const isIssuance = requestType === 'issuance';
  const [faqTopic, setFaqTopic] = useState<FaqTopic | null>(null);
  const [showInstall, setShowInstall] = useState(false);

  const secondaryLabel = onBack ? t('abort.goBack') : t('agentStart.cancel');
  const onSecondary = onBack ?? onCancel;

  if (tags.type === 'Redirect') {
    const redirectUri = (tags.redirect as { uri: string }).uri;
    return (
      <DesktopLayout>
        <div className="flex flex-1 flex-col w-full">
          <div className="flex flex-1 items-center justify-center w-full">
            <div className="flex flex-col items-center gap-6 text-center max-w-lg w-full">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">
                  {t('agentStart.title', { appName })}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t(
                    isIssuance
                      ? 'agentStart.issuanceRedirectSubtitle'
                      : 'agentStart.redirectSubtitle',
                    { appName },
                  )}
                </p>
              </div>

              <DesktopFlowFooter
                primaryLabel={t('agentStart.redirectButton')}
                onPrimary={() => window.location.replace(redirectUri)}
                secondaryLabel={secondaryLabel}
                onSecondary={onSecondary}
              />

              <NeedHelpSection
                topics={['whyRedirection']}
                onSelectTopic={setFaqTopic}
              />
            </div>

            <FaqModal
              topic={faqTopic}
              onChangeTopic={setFaqTopic}
              onClose={() => setFaqTopic(null)}
            />
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout>
      <div className="flex flex-1 flex-col w-full">
        <div className="flex flex-1 items-center justify-center w-full">
          <div className="flex flex-col items-center gap-6 text-center max-w-lg w-full">
            <div>
              <h2 className="text-2xl font-semibold text-foreground">
                {t('agentStart.title', { appName })}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {t(
                  isIssuance
                    ? 'agentStart.issuanceSubtitle'
                    : 'agentStart.subtitle',
                  { appName },
                )}
              </p>
            </div>

            <QrCodeCard
              qrData={tags.type === 'Qr' ? tags.qr.content : ''}
              appLogo={app?.logo}
              appName={appName}
            />

            <button
              className="text-sm text-muted-foreground underline hover:text-foreground transition-colors"
              onClick={() => setShowInstall(true)}
            >
              {t('agentStart.installInstructions')}
            </button>

            <DesktopFlowFooter
              secondaryLabel={secondaryLabel}
              onSecondary={onSecondary}
            />

            <NeedHelpSection
              topics={['whatAreWallets', 'howDataProtected', 'howObtainData']}
              onSelectTopic={setFaqTopic}
            />
          </div>

          <FaqModal
            topic={faqTopic}
            onChangeTopic={setFaqTopic}
            onClose={() => setFaqTopic(null)}
          />
          <InstallInstructionsModal
            open={showInstall}
            appName={app?.name}
            appNameWithArticle={app?.nameWithArticle}
            onClose={() => setShowInstall(false)}
          />
        </div>
      </div>
    </DesktopLayout>
  );
}
